import { visualRect } from "./zoom.js";

// Material-ripple как в tweb: один глобальный pointerdown-делегат,
// без обвязки каждого компонента. Целевые элементы перечислены селектором;
// на акцентных поверхностях волна белая, на остальных — «чернильная».

const RIPPLE_TARGETS = [
  ".button",
  ".hbtn",
  ".cbtn",
  ".hamburger",
  ".icon-button",
  ".ch-menu__item",
  ".chat",
  ".folder",
  ".fab",
  ".mic",
  // .scroll-down намеренно НЕ здесь: его счётчик непрочитанных позиционирован
  // за границами кнопки, а ripple-host обрезает всё по overflow.
  ".theme-picker__option",
  ".account-switcher-list__select",
  ".dialog-context-menu__item",
  ".quick-switcher__item"
].join(", ");

const LIGHT_RIPPLE_TARGETS = [
  ".button--primary",
  ".mic",
  ".fab",
  ".chat.active",
  ".folder.active"
].join(", ");

// Нужен ли элементу position:relative — свойство стабильное (из CSS-класса),
// а getComputedStyle на каждый клик = второй forced style flush в момент
// нажатия. Считаем один раз и запоминаем.
const needsRelCache = new WeakMap<HTMLElement, boolean>();

const spawnRipple = (host: HTMLElement, clientX: number, clientY: number): void => {
  // Оба чтения layout — ДО первой записи в classList: иначе браузер делает
  // два принудительных пересчёта стилей на клик (read → write → read).
  // visualRect: clientX/Y всегда визуальные, rect в Safari — логический.
  const rect = visualRect(host);
  let needsRel = needsRelCache.get(host);
  if (needsRel === undefined) {
    needsRel = getComputedStyle(host).position === "static";
    needsRelCache.set(host, needsRel);
  }
  const x = clientX - rect.left;
  const y = clientY - rect.top;
  // радиус до самого дальнего угла — волна накрывает весь элемент
  const radius = Math.hypot(Math.max(x, rect.width - x), Math.max(y, rect.height - y));

  host.classList.add("ripple-host");
  if (needsRel) {
    host.classList.add("ripple-host--rel");
  }

  const ink = document.createElement("span");
  ink.className = host.matches(LIGHT_RIPPLE_TARGETS) ? "ripple-ink ripple-ink--light" : "ripple-ink";
  ink.style.left = `${x - radius}px`;
  ink.style.top = `${y - radius}px`;
  ink.style.width = `${radius * 2}px`;
  ink.style.height = `${radius * 2}px`;

  // Когда волн не осталось — возвращаем элементу исходный overflow/position,
  // чтобы не влиять на его layout вне момента анимации.
  const cleanup = (): void => {
    ink.remove();
    if (!host.querySelector(".ripple-ink")) {
      host.classList.remove("ripple-host", "ripple-host--rel");
    }
  };

  ink.addEventListener("animationend", cleanup);
  // страховка, если animationend потерялся (display:none и т.п.)
  window.setTimeout(cleanup, 900);
  host.appendChild(ink);
};

export const installRipple = (): void => {
  if (typeof document === "undefined") {
    return;
  }

  document.addEventListener(
    "pointerdown",
    (event) => {
      if (event.button !== 0) {
        return;
      }
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      const host = target.closest<HTMLElement>(RIPPLE_TARGETS);
      if (!host || host.matches(":disabled") || host.getAttribute("aria-disabled") === "true") {
        return;
      }
      if (event.pointerType === "touch") {
        // На таче любой флик по списку начинается с pointerdown над строкой:
        // немедленный spawn = forced layout (visualRect + getComputedStyle)
        // прямо в момент начала скролла. Как в tweb — ждём ~90 мс и отменяем,
        // если палец начал двигаться (скролл-интент).
        const { clientX, clientY, pointerId } = event;
        const timer = window.setTimeout(() => {
          removeListeners();
          spawnRipple(host, clientX, clientY);
        }, 90);
        const cancel = (moveEvent?: PointerEvent): void => {
          if (moveEvent && moveEvent.pointerId !== pointerId) {
            return;
          }
          window.clearTimeout(timer);
          removeListeners();
        };
        const onMove = (moveEvent: PointerEvent): void => cancel(moveEvent);
        const onCancel = (moveEvent: PointerEvent): void => cancel(moveEvent);
        const removeListeners = (): void => {
          document.removeEventListener("pointermove", onMove);
          document.removeEventListener("pointercancel", onCancel);
        };
        document.addEventListener("pointermove", onMove, { passive: true });
        document.addEventListener("pointercancel", onCancel, { passive: true });
        return;
      }
      spawnRipple(host, event.clientX, event.clientY);
    },
    { passive: true }
  );
};
